import { PRODUCTS_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const productsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ============================
    // ADMIN: Catalog Makes (derived from VehicleModel.distinct("make"))
    // Use this for fitment editor dropdowns (catalog)
    // ============================
    adminGetCatalogMakes: builder.query({
      query: () => ({ url: "/api/fitments/catalog/makes" }),
      providesTags: ["FitmentFacets"],
      keepUnusedDataFor: 5,
    }),

    // ============================
    // ADMIN: VehicleMake CRUD (logo/sort/active)
    // Use this for MakeAdminScreen table
    // ============================
    adminListVehicleMakes: builder.query({
      query: () => ({ url: "/api/products/fitments/makes" }),
      providesTags: ["VehicleMakes"],
      keepUnusedDataFor: 5,
    }),

    adminCreateMake: builder.mutation({
      query: (body) => ({
        url: "/api/products/fitments/makes",
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleMakes"],
    }),

    adminUpdateMake: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/products/fitments/makes/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleMakes"],
    }),

    adminDeleteMake: builder.mutation({
      query: (id) => ({
        url: `/api/products/fitments/makes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VehicleMakes"],
    }),

    // ============================
    // Uploads
    // ============================
    uploadImage: builder.mutation({
      query: (data) => ({
        url: "/api/upload",
        method: "POST",
        body: data,
      }),
    }),

    deleteImage: builder.mutation({
      query: (data) => ({
        url: "/api/upload",
        method: "DELETE",
        body: data,
      }),
    }),

    // ============================
    // PUBLIC categories (derived from products)
    // ============================
    getProductCategories: builder.query({
      query: () => ({ url: `${PRODUCTS_URL}/categories` }),
      providesTags: ["Product"],
    }),

    // ============================
    // ADMIN hub categories (from PartCategory with counts)
    // ============================
    getAdminProductCategories: builder.query({
      query: () => ({ url: `${PRODUCTS_URL}/categories/admin` }),
      providesTags: ["Product", "Category"],
    }),

    // ============================
    // LIST / FILTER
    // ============================
    getProducts: builder.query({
      query: (args = {}) => {
        const {
          keyword,
          pageNumber,
          make,
          model,
          year,
          trim,
          engine,
          category,
          position,
          brand,
          brands,
          categories,
          materials,
          minPrice,
          maxPrice,
          status,
          includeComingSoon = true,
          all,
        } = args;

        const params = {};
        if (args.seoCategory) params.seoCategory = args.seoCategory;
        if (keyword) params.keyword = keyword;
        if (pageNumber) params.pageNumber = pageNumber;
        if (make) params.make = make;
        if (model) params.model = model;
        if (year) params.year = year;
        if (trim) params.trim = trim;
        if (engine) params.engine = engine;

        if (category) params.category = category;
        if (position) params.position = position;
        if (brand) params.brand = brand;
        if (brands) params.brands = brands;
        if (categories) params.categories = categories;
        if (materials) params.materials = materials;
        if (minPrice !== undefined) params.minPrice = minPrice;
        if (maxPrice !== undefined) params.maxPrice = maxPrice;
        if (status) params.status = status;
        if (args.descriptionQuality) params.descriptionQuality = args.descriptionQuality;
        if (includeComingSoon) params.includeComingSoon = '1';
        if (all) params.all = '1';

        return { url: PRODUCTS_URL, params };
      },
      providesTags: ["Products"],
      keepUnusedDataFor: 5,
    }),

    // DETAILS
    getProductDetails: builder.query({
      query: (productId) => ({ url: `${PRODUCTS_URL}/${productId}` }),
      keepUnusedDataFor: 5,
    }),

    getRelatedProducts: builder.query({
      query: ({ productId, vehicle } = {}) => {
        const params = {};
        if (vehicle?.make && vehicle?.model && vehicle?.year) {
          params.make = vehicle.make;
          params.model = vehicle.model;
          params.year = vehicle.year;
          if (vehicle.trim) params.trim = vehicle.trim;
        }
        return { url: `${PRODUCTS_URL}/${productId}/related`, params };
      },
      keepUnusedDataFor: 30,
    }),

    // CREATE
    createProduct: builder.mutation({
      query: () => ({ url: PRODUCTS_URL, method: "POST" }),
      invalidatesTags: ["Products"],
    }),

    // UPDATE
    updateProduct: builder.mutation({
      query: (data) => ({
        url: `${PRODUCTS_URL}/${data._id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Products"],
    }),

    // DELETE
    deleteProduct: builder.mutation({
      query: (productId) => ({
        url: `${PRODUCTS_URL}/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Products"],
    }),

    // REVIEW
    createReview: builder.mutation({
      query: (data) => ({
        url: `${PRODUCTS_URL}/${data.productId}/reviews`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Products"],
    }),

    // TOP
    getTopProducts: builder.query({
      query: () => ({ url: `${PRODUCTS_URL}/top` }),
      keepUnusedDataFor: 5,
    }),

    // ============================
    // FITMENT FACETS (public)
    // ============================
    getFitmentFacets: builder.query({
      query: (params = {}) => ({
        url: `${PRODUCTS_URL}/fitments/facets`,
        params: { includeComingSoon: '1', ...params },
      }),
      providesTags: ["FitmentFacets"],
      keepUnusedDataFor: 5,
    }),

    // ============================
    // PUBLIC: Part category tiles for Browse screen
    // ============================
    getPartCategories: builder.query({
      query: () => ({ url: "/api/parts/categories" }),
      providesTags: ["PartCategories"],
      keepUnusedDataFor: 60,
    }),

    // ============================
    // ADMIN: Part categories CRUD
    // ============================
    adminListPartCategories: builder.query({
      query: () => ({ url: "/api/parts/categories/admin" }),
      providesTags: ["PartCategories"],
    }),
    adminCreatePartCategory: builder.mutation({
      query: (body) => ({ url: "/api/parts/categories", method: "POST", body }),
      invalidatesTags: ["PartCategories"],
    }),
    adminUpdatePartCategory: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/parts/categories/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PartCategories"],
    }),
    adminDeletePartCategory: builder.mutation({
      query: (id) => ({ url: `/api/parts/categories/${id}`, method: "DELETE" }),
      invalidatesTags: ["PartCategories"],
    }),

    // ============================
    // ADMIN: Fitment Catalog (Models/Years/Trims/Engines)
    // ============================
    adminListModels: builder.query({
      query: (params = {}) => ({ url: "/api/fitments/catalog/models", params }),
      providesTags: ["FitmentFacets"],
    }),
    adminCreateModel: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/models", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminUpdateModel: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/fitments/catalog/models/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminDeleteModel: builder.mutation({
      query: (id) => ({ url: `/api/fitments/catalog/models/${id}`, method: "DELETE" }),
      invalidatesTags: ["FitmentFacets"],
    }),

    adminListYears: builder.query({
      query: (params = {}) => ({ url: "/api/fitments/catalog/years", params }),
      providesTags: ["FitmentFacets"],
    }),
    adminCreateYear: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/years", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminUpdateYear: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/fitments/catalog/years/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminDeleteYear: builder.mutation({
      query: (id) => ({ url: `/api/fitments/catalog/years/${id}`, method: "DELETE" }),
      invalidatesTags: ["FitmentFacets"],
    }),

    adminListTrims: builder.query({
      query: (params = {}) => ({ url: "/api/fitments/catalog/trims", params }),
      providesTags: ["FitmentFacets"],
    }),
    adminCreateTrim: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/trims", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminUpdateTrim: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/fitments/catalog/trims/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminDeleteTrim: builder.mutation({
      query: (id) => ({ url: `/api/fitments/catalog/trims/${id}`, method: "DELETE" }),
      invalidatesTags: ["FitmentFacets"],
    }),

    adminListEngines: builder.query({
      query: (params = {}) => ({ url: "/api/fitments/catalog/engines", params }),
      providesTags: ["FitmentFacets"],
    }),
    adminCreateEngine: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/engines", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminUpdateEngine: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/fitments/catalog/engines/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminDeleteEngine: builder.mutation({
      query: (id) => ({ url: `/api/fitments/catalog/engines/${id}`, method: "DELETE" }),
      invalidatesTags: ["FitmentFacets"],
    }),

    // Bulk / upsert
    adminUpsertFitment: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/upsert", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),
    adminBulkUpsertFitments: builder.mutation({
      query: (body) => ({ url: "/api/fitments/catalog/bulk", method: "POST", body }),
      invalidatesTags: ["FitmentFacets"],
    }),

    // ============================
    // ADMIN: Stock tools
    // ============================
    adminLowStock: builder.query({
      query: (q = "") => ({
        url: "/api/products/admin/stock/low",
        params: q ? { q } : undefined,
      }),
      providesTags: ["Products"],
    }),

    adminReplenishProduct: builder.mutation({
      query: ({ id, amount }) => ({
        url: `/api/products/admin/stock/${id}/replenish`,
        method: "PUT",
        body: amount ? { amount } : {},
      }),
      invalidatesTags: ["Products"],
    }),

    adminBarcodeLookup: builder.query({
      query: (code) => ({
        url: "/api/products/admin/barcode-lookup",
        params: { code },
      }),
    }),

    adminAssignBarcode: builder.mutation({
      query: (body) => ({
        url: "/api/products/admin/barcode-assign",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Products"],
    }),

    getSaleProducts: builder.query({
      query: (limit = 12) => `/api/products/sale?limit=${limit}`,
      keepUnusedDataFor: 10,
    }),
  }),
});

// Hooks
export const {
  useGetProductCategoriesQuery,
  useGetProductsQuery,
  useGetProductDetailsQuery,
  useGetRelatedProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUploadImageMutation,
  useDeleteImageMutation,
  useDeleteProductMutation,
  useCreateReviewMutation,
  useGetTopProductsQuery,
  useGetFitmentFacetsQuery,
  useGetPartCategoriesQuery,

  useAdminListPartCategoriesQuery,
  useAdminCreatePartCategoryMutation,
  useAdminUpdatePartCategoryMutation,
  useAdminDeletePartCategoryMutation,

  useAdminGetCatalogMakesQuery,
  useAdminListVehicleMakesQuery,

  useAdminCreateMakeMutation,
  useAdminUpdateMakeMutation,
  useAdminDeleteMakeMutation,

  useAdminListModelsQuery,
  useAdminCreateModelMutation,
  useAdminUpdateModelMutation,
  useAdminDeleteModelMutation,

  useAdminListYearsQuery,
  useAdminCreateYearMutation,
  useAdminUpdateYearMutation,
  useAdminDeleteYearMutation,

  useAdminListTrimsQuery,
  useAdminCreateTrimMutation,
  useAdminUpdateTrimMutation,
  useAdminDeleteTrimMutation,

  useAdminListEnginesQuery,
  useAdminCreateEngineMutation,
  useAdminUpdateEngineMutation,
  useAdminDeleteEngineMutation,

  useAdminUpsertFitmentMutation,
  useAdminBulkUpsertFitmentsMutation,

  useGetAdminProductCategoriesQuery,
  useAdminLowStockQuery,
  useAdminReplenishProductMutation,
  useLazyAdminBarcodeLookupQuery,
  useAdminAssignBarcodeMutation,

  useGetSaleProductsQuery,
} = productsApiSlice;
